import { computed, ComputedRef, Ref, ref, watch } from 'vue';
import { OrderType } from '@/enums/OrderEnums';
import type { ICategory } from '@/interfaces/ICategory';
import type { IItem } from '@/interfaces/IItem';
import { timeInsideHours } from '@/utils';
import { useMenuStore } from '@/composition/use-menu';
import { useRestaurantStore } from '@/stores/restaurant.store';

export interface IPosMenuModel {
  isReady: ComputedRef<boolean>;
  menuId: ComputedRef<number | null>;
  categories: ComputedRef<ICategory[]>;
  selectedCategoryId: Ref<number | null>;
  items: ComputedRef<IItem[]>;
  allItems: ComputedRef<IItem[]>;
  /**
   * IDs of items shown as disabled (own `visible` flag off OR the item's
   * category disabled). Single source for greying and the add-click guard;
   * covers search results too.
   */
  disabledItemIds: ComputedRef<Set<number>>;
}

/**
 * Per-order-type menu model (adapted from -pos usePosOrderMenuModel).
 *
 * Each POS V2 tab can hold a different order type, and with `multipleMenus`
 * each type may map to a different menu — so this NEVER touches the global
 * `menuStore.selectedOrderType`/`selectedMenu` (shared with the legacy POS
 * and QR-menu flows). Items are derived from the raw `menuStore.items`
 * (NOT `visibleItems`, which is filtered by the global selectedMenu and
 * would blank/cross-leak items for tabs mapped to another menu), replicating
 * that getter's visibility rules against this model's own category set.
 */
export function usePosMenuModel(type: Ref<OrderType>): IPosMenuModel {
  const menuStore = useMenuStore();
  const restaurantStore = useRestaurantStore();

  const isReady = computed(
    () => menuStore.categories !== null && menuStore.items !== null
  );

  const menuId = computed<number | null>(() => {
    if (!restaurantStore.restaurantSettings?.multipleMenus) return null;
    return menuStore.menuIdByOrderType?.[type.value] ?? null;
  });

  const categories = computed<ICategory[]>(() => {
    // Sourced from the raw list (NOT visibleCategories): POS v2 always shows
    // disabled categories greyed at the end. Branch-hidden ones stay dropped
    // (same rule as menuStore.visibleCategories).
    const raw = menuStore.categories ?? [];
    const branch = restaurantStore.selectedBranch;

    return raw
      .filter((c) => {
        if ((c.menuId ?? null) !== menuId.value) return false;
        if (
          c.hiddenBranchesIds &&
          branch &&
          c.hiddenBranchesIds.indexOf(branch.id + '') !== -1
        ) {
          return false;
        }
        return true;
      })
      .slice()
      .sort(
        (a, b) =>
          (a.visible ? 0 : 1) - (b.visible ? 0 : 1) ||
          (a.index ?? 0) - (b.index ?? 0)
      );
  });

  const categoryIds = computed<Set<number>>(
    () => new Set(categories.value.map((c) => c.id))
  );

  const disabledCategoryIds = computed<Set<number>>(
    () =>
      new Set(categories.value.filter((c) => !c.visible).map((c) => c.id))
  );

  // Replicates menuStore.visibleItems' rules, but against THIS model's
  // per-type category set instead of the global selected menu.
  const visibleItemsForType = computed<IItem[]>(() => {
    const raw = menuStore.items ?? [];
    const branch = restaurantStore.selectedBranch;

    return raw.filter((item) => {
      // Disabled items are kept (greyed + guarded); branch/hour rules and the
      // per-type category set still apply.
      if (!categoryIds.value.has(item.categoryId)) return false;

      if (
        item.hiddenBranchesIds &&
        branch &&
        item.hiddenBranchesIds.indexOf(branch.id + '') !== -1
      ) {
        return false;
      }

      if (restaurantStore.effectiveTier > 0) {
        if (item.hourRanges && branch?.locationId) {
          if (!timeInsideHours(item.hourRanges, branch.location!.tz)) {
            return false;
          }
        }
      }

      return true;
    });
  });

  const selectedCategoryId = ref<number | null>(null);

  // Reset selection when the effective menu changes (type switch).
  watch(menuId, () => {
    selectedCategoryId.value = null;
  });

  // Keep selection valid (auto-select first category when current disappears).
  watch(
    categories,
    (nextCategories) => {
      if (nextCategories.length === 0) {
        selectedCategoryId.value = null;
        return;
      }
      const current = selectedCategoryId.value;
      const stillExists =
        typeof current === 'number' &&
        nextCategories.some((c) => c.id === current);
      if (!stillExists) {
        selectedCategoryId.value = nextCategories[0].id;
      }
    },
    { immediate: true }
  );

  const isEffectivelyDisabled = (item: IItem): boolean =>
    !item.visible || disabledCategoryIds.value.has(item.categoryId);

  const items = computed<IItem[]>(() => {
    const categoryId = selectedCategoryId.value;
    if (!categoryId) return [];

    return visibleItemsForType.value
      .filter((i) => i.categoryId === categoryId)
      .slice()
      .sort(
        (a, b) =>
          (isEffectivelyDisabled(a) ? 1 : 0) -
            (isEffectivelyDisabled(b) ? 1 : 0) ||
          (a.index ?? 0) - (b.index ?? 0)
      );
  });

  // Search corpus across every category of this type's menu.
  const allItems = computed<IItem[]>(() =>
    visibleItemsForType.value
      .slice()
      .sort(
        (a, b) =>
          (isEffectivelyDisabled(a) ? 1 : 0) -
            (isEffectivelyDisabled(b) ? 1 : 0) ||
          a.name.localeCompare(b.name)
      )
  );

  const disabledItemIds = computed<Set<number>>(
    () =>
      new Set(
        visibleItemsForType.value
          .filter((i) => isEffectivelyDisabled(i))
          .map((i) => i.id)
      )
  );

  return {
    isReady,
    menuId,
    categories,
    selectedCategoryId,
    items,
    allItems,
    disabledItemIds,
  };
}
